/**
 * TEAM-HUB-01 — data layer for the Team tab.
 *
 * Reshaped 2026-08-20 (Seth: "Sending messages to the techs should be the
 * primary tool of this tab… we all just need an extremely fast way of
 * seeing the week"):
 *   · Messages — the tab's PRIMARY tool. Announcements, "please contact
 *     this customer" (carries the customer), and general Messages. ANY of
 *     them can carry an attachment — a one-time upload or a Library doc
 *     (with add-to-Library / replace from the composer) — plus an expiry.
 *   · Install week — the weekly install PPTX (08-24-26.pptx: one slide per
 *     day, Installer/Time/Job#/Customer/Equip), LIVE. Every field it carries
 *     is already in the ESC mirror: window_code='INSTAL' legs hold tech,
 *     date, time, customer, and the equip list inside dispatch notes.
 *     Source flips to the Pegasus board at go-live; today ESC is the truth.
 *   · Library — versioned company documents in one searchable place.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

// ── Install week ────────────────────────────────────────────────────────────
export interface InstallLeg {
  dispatch: string;
  counter: string | null;
  appointment_date: string;
  tpromtime: string | null;
  window_code: string | null;
  status: string | null;
  tech_empno: string | null;
  tech_name: string | null;
  custno: string | null;
  customer_fullname: string | null;
  city: string | null;
  notes: string | null;
}

/** Coordinator override for one installer-day. Absence of a row = auto:
 *  the grid computes the opening from what's booked (a lone AM furnace/AC
 *  leaves the PM open, and vice versa; full systems and oddballs — mini
 *  splits, rooftops, garage units — count as the whole day). */
export interface DayOverride {
  day: string;
  empno: string;
  avail: "none" | "am" | "pm" | "day"; // none = day full, no more jobs
  label: string | null; // optional window hint, e.g. "12-4 only"
}

export interface TechAvailRow {
  empno: string;
  kind: string;
  title: string | null;
  start_at: string;
  end_at: string;
  all_day: boolean;
  status: string;
}

export interface InstallWeekData {
  legs: InstallLeg[]; // lane techs' full boards Mon..Sat + every INSTAL leg
  offs: InstallLeg[]; // OFF markers same span (UI shows them on shown lanes)
  /** Pegasus-native time off / meetings (tech_availability) overlapping the week. */
  avail: TechAvailRow[];
  overrides: DayOverride[]; // coordinator says-so beats the auto rules
  crew: { empno: string; name: string }[]; // Settings-picked default lanes
  guests: { day: string; empno: string; name: string }[]; // day-scoped additions
  hides: string[]; // helper lanes ✕'d off for this week (empnos)
  syncedAt: string | null; // freshness stamp — mirror syncs nightly
}

const LEG_COLS =
  "dispatch, counter, appointment_date, tpromtime, window_code, status, tech_empno, tech_name, custno, customer_fullname, city, notes";

const empName = (e: { firstname?: string | null; lastname?: string | null; empname?: string | null; empno: string }) =>
  [e.firstname, e.lastname].filter(Boolean).join(" ").trim() || e.empname || e.empno;

/** The install week, straight from the ESC mirror. weekStartIso = Monday.
 *  Lanes = the Settings-picked crew (pegasus_install_week_crew; falls back
 *  to "anyone installing lately" until the first save) + day-scoped guests.
 *  Legs = those techs' WHOLE boards for the week (a plumbing job blocks the
 *  day just like an install does) + every INSTAL leg regardless of tech. */
export function useInstallWeek(weekStartIso: string) {
  return useQuery({
    queryKey: ["install_week", weekStartIso],
    queryFn: async (): Promise<InstallWeekData> => {
      const end = new Date(weekStartIso + "T12:00:00");
      end.setDate(end.getDate() + 5); // Mon..Sat
      const endIso = end.toLocaleDateString("en-CA");

      const [crewT, guestT, hideT] = await Promise.all([
        supabase.from("pegasus_install_week_crew").select("empno, sort").order("sort").order("empno"),
        supabase.from("pegasus_install_week_guest").select("day, empno")
          .gte("day", weekStartIso).lte("day", endIso),
        supabase.from("pegasus_install_week_hide").select("empno").eq("week_start", weekStartIso),
      ]);
      let crewEmpnos = ((crewT.data ?? []) as { empno: string }[]).map((r) => r.empno);
      if (crewEmpnos.length === 0) {
        // Fallback until Settings > Install Week is saved once: whoever has
        // been on INSTAL legs lately (>=2 in ~15 weeks) is the crew.
        const since = new Date(Date.now() - 70 * 86400000).toLocaleDateString("en-CA");
        const until = new Date(Date.now() + 35 * 86400000).toLocaleDateString("en-CA");
        const { data } = await supabase.from("v_dispatch_full").select("tech_empno")
          .eq("window_code", "INSTAL").gte("appointment_date", since).lte("appointment_date", until);
        const counts = new Map<string, number>();
        for (const r of (data ?? []) as { tech_empno: string | null }[])
          if (r.tech_empno) counts.set(r.tech_empno, (counts.get(r.tech_empno) ?? 0) + 1);
        crewEmpnos = [...counts.entries()].filter(([, n]) => n >= 2).sort((a, b) => b[1] - a[1]).map(([e]) => e);
      }
      const guestRows = (guestT.data ?? []) as { day: string; empno: string }[];
      const laneEmpnos = [...new Set([...crewEmpnos, ...guestRows.map((g) => g.empno)])];

      const legsFilter = laneEmpnos.length
        ? `tech_empno.in.(${laneEmpnos.join(",")}),window_code.eq.INSTAL`
        : "window_code.eq.INSTAL";
      const [legs, offs, ovr, names, sync, availRows] = await Promise.all([
        supabase.from("v_dispatch_full").select(LEG_COLS)
          .or(legsFilter)
          .gte("appointment_date", weekStartIso).lte("appointment_date", endIso)
          .neq("window_code", "OFF")
          .order("appointment_date").order("tpromtime"),
        supabase.from("v_dispatch_full").select(LEG_COLS)
          .eq("window_code", "OFF")
          .gte("appointment_date", weekStartIso).lte("appointment_date", endIso),
        supabase.from("pegasus_install_day_override").select("day, empno, avail, label")
          .gte("day", weekStartIso).lte("day", endIso),
        laneEmpnos.length
          ? supabase.from("mirror_employee").select("empno, firstname, lastname, empname").in("empno", laneEmpnos)
          : Promise.resolve({ data: [] } as { data: { empno: string; firstname: string | null; lastname: string | null; empname: string | null }[] }),
        supabase.from("mirror_dispatch").select("last_synced_at")
          .order("last_synced_at", { ascending: false }).limit(1),
        // TECH-AVAIL on the install board (Seth 2026-08-21: "if Travis has
        // 9/18-9/22 off — will those be marked as off?"): board-added sick/
        // vacation/meetings live in Pegasus (ESC never sees them), so the OFF
        // dispatches above only cover ESC-entered time off. Pull both.
        laneEmpnos.length
          ? supabase.from("tech_availability")
              .select("empno, kind, title, start_at, end_at, all_day, status")
              .in("empno", laneEmpnos)
              .neq("status", "denied")
              .lt("start_at", `${endIso}T23:59:59`)
              .gt("end_at", `${weekStartIso}T00:00:00`)
          : Promise.resolve({ data: [] } as { data: TechAvailRow[] }),
      ]);

      const nameOf = new Map<string, string>();
      for (const e of (names.data ?? []) as { empno: string; firstname: string | null; lastname: string | null; empname: string | null }[])
        nameOf.set(e.empno, empName(e));

      return {
        legs: (legs.data ?? []) as InstallLeg[],
        offs: (offs.data ?? []) as InstallLeg[],
        avail: (availRows.data ?? []) as TechAvailRow[],
        overrides: (ovr.data ?? []) as DayOverride[],
        crew: crewEmpnos.map((e) => ({ empno: e, name: nameOf.get(e) ?? e })),
        guests: guestRows.map((g) => ({ ...g, name: nameOf.get(g.empno) ?? g.empno })),
        hides: ((hideT.data ?? []) as { empno: string }[]).map((h) => h.empno),
        syncedAt: (sync.data?.[0]?.last_synced_at as string | undefined) ?? null,
      };
    },
  });
}

/** Add a tech's board to ONE day of the grid ("Tony has a plumbing job I
 *  want to show for that day"). Remove takes it back off. */
export function useAddInstallGuest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { day: string; empno: string }) => {
      const who = (await supabase.auth.getUser()).data.user?.email ?? "office";
      const { error } = await supabase.from("pegasus_install_week_guest")
        .upsert({ day: v.day, empno: v.empno, added_by: who }, { onConflict: "day,empno" });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["install_week"] }),
  });
}

export function useRemoveInstallGuest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { day: string; empno: string }) => {
      const { error } = await supabase.from("pegasus_install_week_guest")
        .delete().eq("day", v.day).eq("empno", v.empno);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["install_week"] }),
  });
}

/** ✕ on an auto helper lane: hide that tech's lane for ONE week ("8/17
 *  Jeremy — I would never have that on there"). Adding them as a guest or
 *  to the crew always wins over a hide. */
export function useHideInstallLane() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { weekStart: string; empno: string }) => {
      const who = (await supabase.auth.getUser()).data.user?.email ?? "office";
      const { error } = await supabase.from("pegasus_install_week_hide")
        .upsert({ week_start: v.weekStart, empno: v.empno, added_by: who }, { onConflict: "week_start,empno" });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["install_week"] }),
  });
}

/** Settings > Install Week: the default lanes. */
export function useInstallCrew() {
  return useQuery({
    queryKey: ["install_week_crew"],
    queryFn: async (): Promise<{ empno: string; sort: number; name: string }[]> => {
      const { data, error } = await supabase.from("pegasus_install_week_crew")
        .select("empno, sort").order("sort").order("empno");
      if (error) throw error;
      const rows = (data ?? []) as { empno: string; sort: number }[];
      if (!rows.length) return [];
      const { data: emps } = await supabase.from("mirror_employee")
        .select("empno, firstname, lastname, empname").in("empno", rows.map((r) => r.empno));
      const nameOf = new Map((emps ?? []).map((e) => [e.empno, empName(e)]));
      return rows.map((r) => ({ ...r, name: nameOf.get(r.empno) ?? r.empno }));
    },
  });
}

export function useAddInstallCrew() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (empno: string) => {
      const who = (await supabase.auth.getUser()).data.user?.email ?? "office";
      const { data } = await supabase.from("pegasus_install_week_crew").select("sort").order("sort", { ascending: false }).limit(1);
      const next = (data?.[0]?.sort ?? -1) + 1;
      const { error } = await supabase.from("pegasus_install_week_crew")
        .upsert({ empno, sort: next, added_by: who }, { onConflict: "empno" });
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["install_week_crew"] });
      void qc.invalidateQueries({ queryKey: ["install_week"] });
    },
  });
}

export function useRemoveInstallCrew() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (empno: string) => {
      const { error } = await supabase.from("pegasus_install_week_crew").delete().eq("empno", empno);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["install_week_crew"] });
      void qc.invalidateQueries({ queryKey: ["install_week"] });
    },
  });
}

/** Coordinator sets an installer-day's availability. avail 'auto' removes
 *  the override so the computed rules take back over. */
export function useSetDayOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { day: string; empno: string; avail: DayOverride["avail"] | "auto"; label?: string | null }) => {
      const who = (await supabase.auth.getUser()).data.user?.email ?? "office";
      if (v.avail === "auto") {
        const { error } = await supabase.from("pegasus_install_day_override")
          .delete().eq("day", v.day).eq("empno", v.empno);
        if (error) throw error;
        return;
      }
      const { error } = await supabase.from("pegasus_install_day_override").upsert(
        { day: v.day, empno: v.empno, avail: v.avail, label: v.label?.trim() || null, set_by: who, updated_at: new Date().toISOString() },
        { onConflict: "day,empno" },
      );
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["install_week"] }),
  });
}

// ── Library ─────────────────────────────────────────────────────────────────
// Top-level folders offered when nothing else is known (the Messages attach
// picker). LIB-EXPLORER-01: matches the real tree the Drive import built.
export const LIBRARY_FOLDERS = ["Service", "Installation", "Pre-Install", "Sales", "Inventory", "General"] as const;

export interface LibraryDoc {
  id: string;
  folder: string;
  filename: string;
  /** Null for Drive-linked rows imported before their binary migrated (LIB-02). */
  storage_path: string | null;
  mime: string | null;
  size_bytes: number | null;
  version: number;
  active: boolean;
  created_by: string | null;
  created_at: string;
  external_url?: string | null;
  source?: string;
  /** Set when searchable text exists — the "Ask Pegasus can read this" dot. */
  content_extracted_at?: string | null;
  /** ASK-KNOW-01: "file" (an uploaded document) or "note" (written in Pegasus). */
  kind?: string | null;
  updated_at?: string | null;
  updated_by?: string | null;
}

export function useLibraryDocs() {
  return useQuery({
    queryKey: ["library_docs"],
    queryFn: async (): Promise<LibraryDoc[]> => {
      // Explicit columns — content_text can be 20k chars per row and 234 rows
      // of it has no business riding along on every Library open (LIB-03).
      const { data } = await supabase.from("pegasus_library_doc")
        .select("id, folder, filename, storage_path, mime, size_bytes, version, active, created_by, created_at, external_url, source, content_extracted_at, kind, updated_at, updated_by")
        .eq("active", true).order("folder").order("filename");
      return (data ?? []) as LibraryDoc[];
    },
  });
}

// LIB-03: full-text search over the library (same library_search RPC the
// phone + both Ask Pegasus assistants use — ONE index, four doors).
const SEARCH_STOP = new Set([
  "the", "and", "for", "with", "what", "when", "where", "how", "who", "our",
  "are", "can", "does", "this", "that", "have", "has", "need", "from", "was",
  "were", "will", "would", "should", "could", "they", "there", "which",
  "about", "into", "after", "before", "under", "over", "any", "all", "get",
]);
export function searchOrQuery(q: string): string {
  const words = q.toLowerCase().split(/[^a-z0-9$&/.-]+/).filter((w) => w.length >= 3 && !SEARCH_STOP.has(w));
  return [...new Set(words)].slice(0, 12).join(" or ") || q.trim();
}

export interface LibraryHit {
  id: string;
  folder: string;
  filename: string;
  mime: string | null;
  size_bytes: number | null;
  storage_path: string | null;
  external_url: string | null;
  source: string | null;
  rank: number;
  snippet: string | null;
}

export function useLibrarySearch(q: string) {
  const clean = q.trim();
  return useQuery({
    queryKey: ["library_search", clean],
    enabled: clean.length >= 2,
    staleTime: 30 * 1000,
    placeholderData: (prev) => prev,
    queryFn: async (): Promise<LibraryHit[]> => {
      const { data, error } = await supabase.rpc("library_search", { p_q: searchOrQuery(clean), p_limit: 12 });
      if (error) throw error;
      return (data ?? []) as LibraryHit[];
    },
  });
}

/** Fire-and-forget: pull searchable text out of a stored PDF so Ask Pegasus
 *  can answer from it ("who do I refer for duct cleaning?"). */
function extractDocText(docId: string) {
  void supabase.functions.invoke("library-seed", { body: { mode: "extract", doc_id: docId } }).catch(() => {});
}

/** Storage key for a new Library file (the folder path is only a label). */
function libraryStoragePath(folder: string, name: string) {
  const safe = name.replace(/[^A-Za-z0-9._ -]/g, "_");
  const dir = folder.replace(/[^A-Za-z0-9/_ -]/g, "_").replace(/\s+/g, "_") || "General";
  return `library/${dir}/${Date.now()}_${Math.random().toString(36).slice(2, 7)}_${safe}`;
}

/** Upload ONE file into a folder — the plain call the drag-and-drop queue
 *  uses; the hook below wraps it. Returns the new document's id. */
export async function uploadLibraryFile(file: File, folder: string): Promise<string> {
  const who = (await supabase.auth.getUser()).data.user?.email ?? "office";
  const path = libraryStoragePath(folder, file.name);
  const { error: upErr } = await supabase.storage.from("customer-files").upload(path, file, { contentType: file.type || "application/octet-stream" });
  if (upErr) throw upErr;
  const { data: row, error } = await supabase.from("pegasus_library_doc").insert({
    folder, filename: file.name, storage_path: path,
    mime: file.type || null, size_bytes: file.size, created_by: who,
    kind: "file", source: "upload", updated_at: new Date().toISOString(), updated_by: who,
  }).select("id").single();
  if (error) throw error;
  const id = (row as { id: string }).id;
  extractDocText(id);
  return id;
}

/** REPLACE an existing document with a new file — same row (so anything
 *  pointing at it keeps working), version bumps, old file stays in storage
 *  as history, searchable text re-extracts. This is the no-duplicates path
 *  (Seth 2026-08-20: "I don't want the documents getting messy"). */
export async function replaceLibraryFile(doc: LibraryDoc, file: File): Promise<{ filename: string; version: number }> {
  const who = (await supabase.auth.getUser()).data.user?.email ?? "office";
  const path = libraryStoragePath(doc.folder, file.name);
  const { error: upErr } = await supabase.storage.from("customer-files").upload(path, file, { contentType: file.type || "application/octet-stream" });
  if (upErr) throw upErr;
  const { error } = await supabase.from("pegasus_library_doc").update({
    storage_path: path,
    filename: file.name,
    mime: file.type || null,
    size_bytes: file.size,
    version: doc.version + 1,
    // LIB-03: replacing a Drive-link row pulls the doc fully into Pegasus
    // — phones read it in-app from then on, no Google sign-in.
    external_url: null,
    source: "upload",
    // Keep the OLD searchable text until the new extract overwrites it —
    // a non-PDF replacement would otherwise turn a searchable doc silently
    // invisible to Ask Pegasus. extracted_at nulls to mark it stale.
    content_extracted_at: null,
    updated_at: new Date().toISOString(),
    updated_by: who,
  }).eq("id", doc.id);
  if (error) throw error;
  extractDocText(doc.id);
  return { filename: file.name, version: doc.version + 1 };
}

/** Everything a Library change can make stale. */
function invalidateLibrary(qc: ReturnType<typeof useQueryClient>) {
  return Promise.all([
    qc.invalidateQueries({ queryKey: ["library_docs"] }),
    qc.invalidateQueries({ queryKey: ["library_folders"] }),
    qc.invalidateQueries({ queryKey: ["library_archived"] }),
    qc.invalidateQueries({ queryKey: ["library_search"] }),
    qc.invalidateQueries({ queryKey: ["library_stats"] }),
  ]);
}
export function useInvalidateLibrary() {
  const qc = useQueryClient();
  return () => invalidateLibrary(qc);
}

export function useUploadLibraryDoc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { file: File; folder: string }) => uploadLibraryFile(v.file, v.folder),
    onSuccess: () => invalidateLibrary(qc),
  });
}

export function useReplaceLibraryDoc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { doc: LibraryDoc; file: File }) => replaceLibraryFile(v.doc, v.file),
    onSuccess: () => invalidateLibrary(qc),
  });
}

// ── LIB-EXPLORER-01: folders, bulk moves, the Archived bin ──────────────────
// Seth 2026-09-24: "make the library feel a little bit more like what it feels
// like browsing Google Drive / windows explorer folder system". A folder is the
// path on each document ("Service/Triangle Tube"); pegasus_library_folder only
// keeps folders that have nothing in them yet.

/** Empty folders made with New Folder (paths). */
export function useLibraryFolders() {
  return useQuery({
    queryKey: ["library_folders"],
    queryFn: async (): Promise<string[]> => {
      const { data, error } = await supabase.from("pegasus_library_folder").select("path").order("path");
      if (error) throw error;
      return ((data ?? []) as { path: string }[]).map((r) => r.path);
    },
  });
}

export function useCreateLibraryFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (path: string) => {
      const who = (await supabase.auth.getUser()).data.user?.email ?? "office";
      const { error } = await supabase.from("pegasus_library_folder").upsert({ path, created_by: who }, { onConflict: "path", ignoreDuplicates: true });
      if (error) throw error;
    },
    onSuccess: () => invalidateLibrary(qc),
  });
}

/** Forget empty-folder records (the folder itself disappears once nothing is
 *  left in it). Pass the folder and every subfolder path to remove. */
export function useForgetLibraryFolders() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (paths: string[]) => {
      if (!paths.length) return;
      const { error } = await supabase.from("pegasus_library_folder").delete().in("path", paths);
      if (error) throw error;
    },
    onSuccess: () => invalidateLibrary(qc),
  });
}

/** Rename OR move a folder — its documents and subfolders come along. */
export function useMoveLibraryFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { from: string; to: string }) => {
      const { data, error } = await supabase.rpc("library_move_folder", { p_from: v.from, p_to: v.to });
      if (error) throw error;
      return Number(data ?? 0);
    },
    onSuccess: () => invalidateLibrary(qc),
  });
}

/** Move documents into a folder. */
export function useMoveLibraryDocs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { ids: string[]; folder: string }) => {
      if (!v.ids.length) return;
      const { error } = await supabase.from("pegasus_library_doc")
        .update({ folder: v.folder, updated_at: new Date().toISOString() }).in("id", v.ids);
      if (error) throw error;
    },
    onSuccess: () => invalidateLibrary(qc),
  });
}

/** Archive (Drive's "move to trash") or restore documents. Nothing is ever
 *  deleted from here — Archived keeps them, one click brings them back. */
export function useSetLibraryDocsActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { ids: string[]; active: boolean }) => {
      if (!v.ids.length) return;
      const { error } = await supabase.from("pegasus_library_doc")
        .update({ active: v.active, updated_at: new Date().toISOString() }).in("id", v.ids);
      if (error) throw error;
    },
    onSuccess: () => invalidateLibrary(qc),
  });
}

/** The Archived bin — only fetched while it's open. */
export function useArchivedLibraryDocs(enabled: boolean) {
  return useQuery({
    queryKey: ["library_archived"],
    enabled,
    queryFn: async (): Promise<LibraryDoc[]> => {
      const { data, error } = await supabase.from("pegasus_library_doc")
        .select("id, folder, filename, storage_path, mime, size_bytes, version, active, created_by, created_at, external_url, source, content_extracted_at, kind, updated_at, updated_by")
        .eq("active", false).order("updated_at", { ascending: false }).limit(500);
      if (error) throw error;
      return (data ?? []) as LibraryDoc[];
    },
  });
}

/** The words Pegasus read out of a file — the preview for Word / Excel /
 *  PowerPoint, which a browser can't draw itself. */
export function useLibraryDocText(id: string | null) {
  return useQuery({
    queryKey: ["library_doc_text", id],
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<string | null> => {
      const { data, error } = await supabase.from("pegasus_library_doc").select("content_text").eq("id", id as string).maybeSingle();
      if (error) throw error;
      return ((data as { content_text: string | null } | null)?.content_text ?? null) || null;
    },
  });
}

// ── ASK-KNOW-01: notes — short answers written right in Pegasus ──────────
// Seth 2026-09-23: "What if there is something we want to have added to the
// ask pegasus knowledge base or update or revise or change?" A note is a
// Library row with kind "note": a title (usually the question) and the
// answer in content_text. Ask Pegasus reads notes first; the phones read
// them in-app; editing one updates it everywhere at once.
export interface LibraryNote {
  id: string;
  folder: string;
  filename: string;
  content_text: string | null;
  updated_at: string | null;
  updated_by: string | null;
  created_by: string | null;
}

export function useLibraryNote(id: string | null) {
  return useQuery({
    queryKey: ["library_note", id],
    enabled: !!id,
    queryFn: async (): Promise<LibraryNote | null> => {
      const { data, error } = await supabase.from("pegasus_library_doc")
        .select("id, folder, filename, content_text, updated_at, updated_by, created_by")
        .eq("id", id as string).maybeSingle();
      if (error) throw error;
      return (data as LibraryNote) ?? null;
    },
  });
}

export function useSaveLibraryNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id?: string | null; title: string; folder: string; body: string }): Promise<string> => {
      const who = (await supabase.auth.getUser()).data.user?.email ?? "office";
      const now = new Date().toISOString();
      const body = v.body.trim();
      const row = {
        folder: v.folder.trim() || "Notes",
        filename: v.title.trim(),
        content_text: body,
        content_extracted_at: now,
        mime: "text/plain",
        size_bytes: new Blob([body]).size,
        kind: "note",
        source: "note",
        updated_at: now,
        updated_by: who,
      };
      if (v.id) {
        const { error } = await supabase.from("pegasus_library_doc").update(row).eq("id", v.id);
        if (error) throw error;
        return v.id;
      }
      const { data, error } = await supabase.from("pegasus_library_doc")
        .insert({ ...row, created_by: who }).select("id").single();
      if (error) throw error;
      return (data as { id: string }).id;
    },
    onSuccess: (id) => Promise.all([
      qc.invalidateQueries({ queryKey: ["library_docs"] }),
      qc.invalidateQueries({ queryKey: ["library_search"] }),
      qc.invalidateQueries({ queryKey: ["library_note", id] }),
      qc.invalidateQueries({ queryKey: ["library_stats"] }), // Settings → Ask Pegasus counts
    ]),
  });
}

export function useRenameLibraryDoc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; filename?: string; folder?: string }) => {
      const patch: Record<string, unknown> = { updated_at: new Date().toISOString() };
      if (v.filename != null) patch.filename = v.filename;
      if (v.folder != null) patch.folder = v.folder;
      const { error } = await supabase.from("pegasus_library_doc").update(patch).eq("id", v.id);
      if (error) throw error;
    },
    onSuccess: () => invalidateLibrary(qc),
  });
}

export function useArchiveLibraryDoc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("pegasus_library_doc").update({ active: false, updated_at: new Date().toISOString() }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => invalidateLibrary(qc),
  });
}

/** Rename a folder — its documents AND its subfolders move with it
 *  (LIB-EXPLORER-01: the library_move_folder RPC; the old version left
 *  "Service/Triangle Tube" behind when "Service" was renamed). */
export function useRenameLibraryFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { from: string; to: string }) => {
      const to = v.to.trim();
      if (!to || to === v.from) return;
      const { error } = await supabase.rpc("library_move_folder", { p_from: v.from, p_to: to });
      if (error) throw error;
    },
    onSuccess: () => invalidateLibrary(qc),
  });
}

/** A short-lived link to a stored Library file. `download` = the file name to
 *  save as (the browser downloads instead of opening it). */
export async function libraryDocUrl(path: string, download?: string): Promise<string | null> {
  const { data } = await supabase.storage.from("customer-files")
    .createSignedUrl(path, download ? 120 : 600, download ? { download } : undefined);
  return data?.signedUrl ?? null;
}

// ── Time-off approvals (TIMEOFF-01) ─────────────────────────────────────────
// Requests come in from the field app (tech_availability status='requested',
// self_reported) — a Pegasus system DM already pinged every approver. This is
// the approve/deny surface's data.
export interface TimeOffRequest {
  id: string;
  empno: string;
  kind: string;
  start_at: string;
  end_at: string;
  all_day: boolean;
  title: string | null;
  status: string;
  created_at: string;
}

export function useTimeOffRequests() {
  return useQuery({
    queryKey: ["timeoff_requests"],
    refetchInterval: 60 * 1000,
    queryFn: async (): Promise<TimeOffRequest[]> => {
      const { data, error } = await supabase.from("tech_availability")
        .select("id, empno, kind, start_at, end_at, all_day, title, status, created_at")
        .eq("status", "requested")
        .gt("end_at", new Date().toISOString())
        .order("start_at");
      if (error) throw error;
      return (data ?? []) as TimeOffRequest[];
    },
  });
}

export function useDecideTimeOff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; decision: "approved" | "denied"; note?: string }) => {
      const who = (await supabase.auth.getUser()).data.user?.email ?? "office";
      const { error } = await supabase.from("tech_availability").update({
        status: v.decision,
        approved_by: who,
        decided_at: new Date().toISOString(),
        decision_note: v.note?.trim() || null,
        updated_at: new Date().toISOString(),
      }).eq("id", v.id);
      if (error) throw error;
      // The timeoff_decision DB trigger DMs the tech the verdict by itself.
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["timeoff_requests"] });
      void qc.invalidateQueries({ queryKey: ["tech_availability"] });
      void qc.invalidateQueries({ queryKey: ["tech_availability_upcoming"] });
    },
  });
}

// ── Messages ────────────────────────────────────────────────────────────────
export type MessageKind = "note" | "contact_customer" | "message";

export interface Bulletin {
  id: string;
  audience: string;
  body: string;
  kind: MessageKind;
  custno: string | null;
  customer_label: string | null;
  doc_id: string | null;
  doc_label: string | null;
  attachment_path: string | null; // one-time file (not in the Library)
  attachment_name: string | null;
  expires_on: string | null;
  created_by: string | null;
  created_at: string;
}

export function useBulletins() {
  return useQuery({
    queryKey: ["team_bulletins"],
    queryFn: async (): Promise<Bulletin[]> => {
      const { data } = await supabase.from("pegasus_team_message").select("*").order("created_at", { ascending: false }).limit(50);
      return (data ?? []) as Bulletin[];
    },
  });
}

/** audience: 'techs' = everyone; or a comma-joined list of empnos for a
 *  targeted message ("Andrew + Terry only"). The field app delivers (and
 *  writes receipts) to exactly this audience when it ships; the history is
 *  correct from day one.
 *
 *  Attachments (any kind of message):
 *   · attachLibraryDocId — point at an existing Library doc.
 *   · file — a fresh upload. One-time by default (stored on the message
 *     only); with addToLibrary it ALSO lands in the Library in the chosen
 *     folder (new folder names welcome), and if replaceDoc is set the
 *     upload REPLACES that doc (version bump, same no-duplicates path the
 *     Library uses). */
export function useSendBulletin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: {
      body: string;
      audience: "techs" | string[];
      kind?: MessageKind;
      custno?: string | null;
      customerLabel?: string | null;
      attachLibraryDocId?: string | null;
      attachLibraryDocLabel?: string | null;
      file?: File | null;
      addToLibrary?: { folder: string; replaceDoc?: LibraryDoc | null } | null;
      expiresOn?: string | null;
    }) => {
      const who = (await supabase.auth.getUser()).data.user?.email ?? "office";
      const audience = v.audience === "techs" ? "techs" : v.audience.join(",");

      let docId = v.attachLibraryDocId ?? null;
      let docLabel = v.attachLibraryDocLabel ?? null;
      let attachmentPath: string | null = null;
      let attachmentName: string | null = null;

      if (v.file) {
        const safe = v.file.name.replace(/[^A-Za-z0-9._ -]/g, "_");
        if (v.addToLibrary) {
          // Into the Library (and the message points at the doc).
          const folder = v.addToLibrary.folder.trim() || "General";
          const path = `library/${folder.replace(/\s+/g, "_")}/${Date.now()}_${safe}`;
          const { error: upErr } = await supabase.storage.from("customer-files")
            .upload(path, v.file, { contentType: v.file.type || "application/octet-stream" });
          if (upErr) throw upErr;
          const rep = v.addToLibrary.replaceDoc;
          if (rep) {
            const { error } = await supabase.from("pegasus_library_doc").update({
              storage_path: path, filename: v.file.name, mime: v.file.type || null,
              size_bytes: v.file.size, version: rep.version + 1,
              content_text: null, content_extracted_at: null,
              updated_at: new Date().toISOString(),
            }).eq("id", rep.id);
            if (error) throw error;
            docId = rep.id;
          } else {
            const { data: row, error } = await supabase.from("pegasus_library_doc").insert({
              folder, filename: v.file.name, storage_path: path,
              mime: v.file.type || null, size_bytes: v.file.size, created_by: who,
            }).select("id").single();
            if (error) throw error;
            docId = row?.id ?? null;
          }
          docLabel = v.file.name;
          if (docId) void supabase.functions.invoke("library-seed", { body: { mode: "extract", doc_id: docId } }).catch(() => {});
        } else {
          // One-time: rides on the message only, never clutters the Library.
          const path = `team-messages/${Date.now()}_${safe}`;
          const { error: upErr } = await supabase.storage.from("customer-files")
            .upload(path, v.file, { contentType: v.file.type || "application/octet-stream" });
          if (upErr) throw upErr;
          attachmentPath = path;
          attachmentName = v.file.name;
        }
      }

      const { error } = await supabase.from("pegasus_team_message").insert({
        audience,
        body: v.body,
        created_by: who,
        kind: v.kind ?? "note",
        custno: v.custno ?? null,
        customer_label: v.customerLabel ?? null,
        doc_id: docId,
        doc_label: docLabel,
        attachment_path: attachmentPath,
        attachment_name: attachmentName,
        expires_on: v.expiresOn ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["team_bulletins"] });
      void qc.invalidateQueries({ queryKey: ["library_docs"] });
    },
  });
}
